import {
	Box,
	Button,
	HStack,
	Heading,
	Skeleton,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	VStack
} from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { HiChevronLeft } from "react-icons/hi";
import { Location, generatePath, useLocation, useNavigate, useParams } from "react-router-dom";

// Components
import DataCardDisplay from "../../../../shared/DataDisplay/DataDisplay";
import DataCardDisplayDocs from "../../../../shared/DataDisplayDocs/DataCardDisplayDocs";
import DataCardDisplayStatement from "../../../../shared/DataDisplayStatement";
import LoadingSpinner from "../../../../shared/LoadingSpinner/LoadingSpinner";
import SectionHeader from "../../../../shared/SectionHeader/SectionHeader";
import LayoutWrapper from "../../../../shared/layout";
import { Page } from "../../../../shared/layout/index.styles";
import ClaimPaymentsTable from "../../payments/ClaimPaymentsTable";

// Providers
import { useClaimDetails } from "../../../../context-providers/claims-details-provider";

// Utils
import { RouterPathMapping } from "../../../../Router";
import { ClaimStatus } from "../../../../utils/consts";
import { transformClaimToDisplayStatementData, processClaimStatusForAttorney } from "../../../../utils/helpers";

interface ClaimsDetailProps {}

const AttorneyClaimsDetail: FunctionComponent<ClaimsDetailProps> = (): ReactElement => {
	const { id } = useParams();
	const { claim, handleFetchClaim, getServiceLabel } = useClaimDetails();
	const navigate = useNavigate();
	const { state } = useLocation() as Location<{ from: string }>;
	const [isInitialLoading, setInitialLoading] = useState(true);

	const TabNavigation = ["Details", "Payments"];

	useEffect(() => {
		if (id) {
			const parsedId = parseInt(id);
			handleFetchClaim(parsedId).finally(() => setInitialLoading(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	return (
		<LayoutWrapper>
			<HStack height="100%" overflow="hidden" gap="0">
				{isInitialLoading && <LoadingSpinner />}
				<Box width="100%" height="100%" overflow="auto">
					<Box mx={8}>
						<Page>
							{/* Header Section */}
							<HStack alignItems="center" margin="0 auto">
								<Button
									leftIcon={<HiChevronLeft />}
									variant="ghost"
									onClick={() => navigate(state?.from ?? RouterPathMapping.AttorneyCases)}
									mr="80px">
									Back
								</Button>
								<Heading fontSize="21px" fontWeight="700" color="gray.700" flex={1} textAlign="center">
									Claim {id ? "#" + id : ""}:&nbsp;
									{/* {claim?.atty ? (claim?.caseId ? ", " : "for ") : ""} */}
									{claim?.atty ? claim?.atty.getName() : ""}
								</Heading>
								<Box maxWidth="200px" display="flex" flexDirection="column" alignItems="flex-end">
									<Box display="flex" justifyContent="right" mb="2">
										<Button isDisabled={true}>{processClaimStatusForAttorney(claim?.status)}</Button>
									</Box>
									{claim?.status === ClaimStatus.Draft && (
										<Button
											size="sm"
											colorScheme="orange"
											padding={2}
											onClick={() =>
												navigate(
													generatePath(RouterPathMapping.Attorney_ClaimIntake, {
														id: claim.claimId?.toString() ?? null
													})
												)
											}>
											Continue
										</Button>
									)}
								</Box>
							</HStack>

							{/* Tab Navigation */}
							<Tabs colorScheme="orange" align="center" width="100%" margin="5px auto">
								<TabList
									display="flex"
									justifyContent="center"
									gap="22px"
									borderColor="chakra-border-color"
									borderBottomWidth="2px"
									borderBottomStyle="solid">
									{TabNavigation.map((tab) => (
										<Tab
											key={tab}
											fontSize="18px"
											fontWeight="400"
											lineHeight="normal"
											textAlign="center"
											color="gray.700"
											mb="-2px"
											position="relative"
											_selected={{
												color: "orange.600", // Selected color
												borderBottom: "2px solid",
												borderBottomColor: "orange.600"
											}}>
											{tab}
										</Tab>
									))}
								</TabList>

								<TabPanels>
									<TabPanel p={0} pt={4}>
										<VStack spacing={5} align="stretch">
											{/* Claim Information Section */}
											<Box>
												<SectionHeader title="Claim Information" width="100%" />
												<Skeleton isLoaded={!!claim} endColor="gray.200">
													<DataCardDisplay
														data={[
															{
																title: "PASS Case ID",
																value: `#${claim?.caseId}`,
																isurl: true,
																url: generatePath(RouterPathMapping.Attorney_CaseDetail, {
																	id: claim?.caseId?.toString() ?? ""
																})
															},
															{
																title: "Member",
																value: claim?.member?.getName()
																	? `${claim?.member?.getName()} #${claim?.member?.getId()}`
																	: "",
																isurl: true
															},
															{
																title: "Attorney",
																value: `${claim?.atty?.getName()} #${claim?.atty?.getId()}`,
																isurl: true
															},
															{
																title: "Date of Initial Consultation",
																value: claim ? new Date(claim.dateOfInitialConsultation).toLocaleDateString() : ""
															},
															{
																title: "Final Date of Service",
																value: claim ? new Date(claim.finalDateOfService).toLocaleDateString() : ""
															},
															{
																title: "Received On",
																value: claim?.receivedOn?.toLocaleDateString() ?? ""
															},
															{
																title: "Submitted",
																value: claim?.submittedAt ? new Date(claim.submittedAt).toLocaleDateString() : ""
															},
															{
																title: "Approved",
																value: claim?.approvedAt ? new Date(claim.approvedAt).toLocaleDateString() : ""
															}
														]}
														style={{ width: "100%" }}
														leftSideRows={3}
														leftLabelMinWidth={110}
														rightLabelMinWidth={210}
													/>
												</Skeleton>
											</Box>

											{/* Services & Billing Section */}
											<Box>
												<SectionHeader title="Services & Billing" width="100%" />
												<Skeleton isLoaded={!!claim} endColor="gray.200">
													<DataCardDisplayStatement
														data={claim ? transformClaimToDisplayStatementData(claim, getServiceLabel) : undefined}
														style={{ width: "100%" }}
													/>
												</Skeleton>
											</Box>

											{/* Documents Section */}
											<Box>
												<SectionHeader title="Documents" width="100%" />
												<Skeleton isLoaded={!!claim} endColor="gray.200">
													<DataCardDisplayDocs
														claim={claim}
														style={{ width: "100%", alignItems: "start", textAlign: "left" }}
														isPermanentDeleteEnabled={false}
														isProvider={true}
													/>
												</Skeleton>
											</Box>
										</VStack>
									</TabPanel>
									{/* <TabPanel>
										<Box>Policy content goes here...</Box>
									</TabPanel> */}
									<TabPanel>{claim && claim.caseId && <ClaimPaymentsTable caseId={claim?.caseId} />}</TabPanel>
								</TabPanels>
							</Tabs>
						</Page>
					</Box>
				</Box>
			</HStack>
		</LayoutWrapper>
	);
};

export default AttorneyClaimsDetail;
